// Lightweight in-house i18n — the prototype used i18next from a CDN, which we
// can't ship under the app's no-CDN / CSP constraint. This provides the same
// 5 languages (de/en/es/it/ar, German default, Arabic RTL) from bundled
// per-language files under `locales/`, with `{{var}}` interpolation and a
// React context.
//
// The translation values started as the archived prototype strings, extracted
// verbatim; copy edits since then live in `locales/<lang>.json`, one flat
// key → string map per language. To change a text, edit the value in every
// language file; never add or remove a key in only one of them (the
// `translations` table below is typed against the `en` key set, and
// `test/i18n.test.ts` checks that placeholders and inline tags stay in sync).
//
// `screens.json` next to this file maps every key to the screen it appears on;
// a new key needs an entry there too (`test/i18n-screens.test.ts`).

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import ar from "./locales/ar.json";
import de from "./locales/de.json";
import en from "./locales/en.json";
import es from "./locales/es.json";
import it from "./locales/it.json";

export const LANGS = ["de", "en", "es", "it", "ar"] as const;
export type Lang = (typeof LANGS)[number];

/**
 * The languages a customer can pick (operator decision 2026-10-02). The other
 * three stay translated, tested and accepted by the copy preview; they are
 * only hidden from the switcher and from detection.
 */
export const OFFERED_LANGS = ["de", "en"] as const satisfies readonly Lang[];

/** German is the source language and the copy preview's default. Customers
 *  without a matching browser language get English, see `detectLang`. */
export const DEFAULT_LANG: Lang = "de";

/** Keys are whatever `en` defines — every language shares the same set. */
export type TransKey = keyof typeof en;

/**
 * All five tables, keyed by language. Typed against the `en` key set, so a
 * key missing from any other language file fails `tsc` before it can ship.
 */
export const translations: Record<Lang, Record<TransKey, string>> = {
  de,
  en,
  es,
  it,
  ar,
};

export const LANG_LABELS: Record<Lang, string> = {
  de: "🇩🇪 Deutsch",
  en: "🇬🇧 English",
  es: "🇪🇸 Español",
  it: "🇮🇹 Italiano",
  ar: "🇸🇦 العربية",
};

export const LANG_FLAGS: Record<Lang, string> = {
  de: "🇩🇪",
  en: "🇬🇧",
  es: "🇪🇸",
  it: "🇮🇹",
  ar: "🇸🇦",
};

const STORAGE_KEY = "hezo-scan-lang";

function isOffered(v: string | null | undefined): v is Lang {
  return !!v && (OFFERED_LANGS as readonly string[]).includes(v);
}

/**
 * Pick the initial language: a previously-saved choice, else the first
 * `navigator.languages` entry we offer, else English (operator decision
 * 2026-10-02: a customer whose browser asks for neither de nor en, or for
 * nothing, reads English). A choice saved before a language was hidden
 * (e.g. `es`) is ignored.
 */
export function detectLang(): Lang {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (isOffered(saved)) return saved;
  } catch {
    /* localStorage unavailable (private mode / SSR) — fall through */
  }
  const navs =
    typeof navigator !== "undefined"
      ? (navigator.languages ?? [navigator.language]).filter(Boolean)
      : [];
  for (const l of navs) {
    const two = l.slice(0, 2).toLowerCase();
    if (isOffered(two)) return two;
  }
  return "en";
}

export function isRtl(lang: Lang): boolean {
  return lang === "ar";
}

/** Substitute `{{var}}` occurrences. Exported for the copy preview, which
 *  renders strings that never went through `translations`. */
export function interpolate(s: string, vars?: Record<string, string | number>): string {
  if (!vars) return s;
  let out = s;
  for (const [k, v] of Object.entries(vars)) {
    out = out.replace(new RegExp(`{{\\s*${k}\\s*}}`, "g"), String(v));
  }
  return out;
}

/** Look up a key for `lang`, interpolate `{{var}}`, fall back to en → key. */
export function translate(
  lang: Lang,
  key: TransKey,
  vars?: Record<string, string | number>,
): string {
  const table = translations[lang] as Record<string, string>;
  const s = table?.[key] ?? (translations.en as Record<string, string>)[key] ?? String(key);
  return interpolate(s, vars);
}

interface I18nContextValue {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: (key: TransKey, vars?: Record<string, string | number>) => string;
  dir: "ltr" | "rtl";
}

const I18nContext = createContext<I18nContextValue | null>(null);

export function I18nProvider({
  children,
  initialLang,
  controlledLang,
  overrides,
}: {
  children: ReactNode;
  /** Force a language (tests / deterministic rendering); else auto-detect. */
  initialLang?: Lang;
  /** Copy preview only: the language is owned by the embedding editor, so the
   *  in-app switcher must not be able to drift away from it. When set, this
   *  wins over the internal state and nothing is persisted. */
  controlledLang?: Lang;
  /** Copy preview only: unsaved texts from the editor, key → string, on top of
   *  the bundled locale. Missing keys fall through to the shipped value, so a
   *  half-filled editor still renders a complete screen. */
  overrides?: Readonly<Record<string, string>>;
}): React.ReactElement {
  const [lang, setLangState] = useState<Lang>(() => initialLang ?? detectLang());
  const active = controlledLang ?? lang;

  function setLang(l: Lang): void {
    setLangState(l);
    try {
      localStorage.setItem(STORAGE_KEY, l);
    } catch {
      /* ignore persistence failures */
    }
  }

  // Keep the document's lang + direction in sync (Arabic → RTL).
  useEffect(() => {
    document.documentElement.lang = active;
    document.documentElement.dir = isRtl(active) ? "rtl" : "ltr";
  }, [active]);

  const value = useMemo<I18nContextValue>(
    () => ({
      lang: active,
      setLang,
      t: (key, vars) => {
        const edited = overrides?.[key];
        return edited != null ? interpolate(edited, vars) : translate(active, key, vars);
      },
      dir: isRtl(active) ? "rtl" : "ltr",
    }),
    [active, overrides],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within <I18nProvider>");
  return ctx;
}
